/**
 * Constantes routes typées — Phase 4.I.1
 * Usage : navigate(ROUTES.marcheRetail)
 */
import type { CleI18n } from '@i18n';

export const ROUTES = {
  root: '/' as const,
  home: '/' as const,        // DS.12 Hero Home V3 (V2.4-bis)
  login: '/login' as const,
  twoFactor: '/2fa' as const,
  twoFactorEnroll: '/2fa/enrolement' as const,   // `#551` — enrolement force

  // Marché — VF.REFONTE-MODULES-B (DECISIONS #153)
  // marcheHome supprimé en vague dettes 2 (2026-07-27) — la page a été fusionnée
  // dans PhenomenaMarketPage (/phenomena/marche). Redirect compat conservé dans
  // router.tsx pour bookmarks/liens externes.
  marche:       '/marche' as const,          // URL canonique unifiée Retail / Wholesale
  marcheRetail: '/marche/retail' as const,   // Legacy — redirect compat → /marche
  // marcheWholesale supprimé — redirect compat /marche/wholesale → /marche?channel=wholesale

  // Performance
  performance: '/performance' as const,
  // Ancien pattern `/performance/merch/:merchCode` retiré en P9 P5 —
  // la fiche merch est désormais un drawer (merch360Store). DECISIONS #285.

  // Comparateur
  comparator: '/comparator' as const,

  // Lancements officiels (S10) — module AUTONOME, hors MX RM Flow.
  officialLaunches: '/official-launches' as const,

  // Brand vs Brand (placeholder — page stub)
  brandVsBrand: '/brand-vs-brand' as const,

  // Self Service Lab
  lab:    '/lab' as const,
  labHub: '/lab/hub' as const,

  // Geo-data (VF.GEO.1)
  geoData: '/geo-data' as const,

  // Flash Info
  flashinfoFeed: '/flashinfo/feed' as const,
  flashinfoCreate: '/flashinfo/create' as const,
  flashinfoEdit: (id: string) => `/flashinfo/edit/${id}` as const,
  flashinfoEditPattern: '/flashinfo/edit/:id' as const,
  /**
   * ⚠ LA ROUTE DÉTAIL `/flashinfo/:id` A ÉTÉ SUPPRIMÉE — décision Hkm, franche.
   *   La fiche s'ouvre désormais en OVERLAY sur le feed, pas sur une page.
   *
   * ⚠ CONSÉQUENCE ASSUMÉE, PAS UN OUBLI : un lien externe ou un favori pointant
   *   sur l'ancienne `/flashinfo/<uuid>` ne rend PAS une 404 — il tombe dans le
   *   catch-all `router.tsx` (`path="*"`) et atterrit SUR L'ACCUEIL, SANS AUCUN
   *   MESSAGE. C'est connu, mesuré et accepté. Aucune route de compatibilité
   *   n'a été laissée : si quelqu'un la rétablit un jour, qu'il sache qu'il
   *   revient sur une décision, pas qu'il répare un manque.
   */
  flashinfoFeedCarte: (id: string) => `/flashinfo/feed?carte=${id}` as const,

  // Référentiels
  referentielsPdv:           '/referentiels/pdv' as const,
  referentielsSku:           '/referentiels/sku' as const,
  referentielsBrands:        '/referentiels/brands' as const,
  referentielsMerchandisers: '/referentiels/merchandisers' as const,
  referentielsRoutings:      '/referentiels/routings' as const,

  // Admin
  adminSegmentation:                  '/admin/segmentation' as const,
  adminSegmentationFactorsCatalogue:  '/admin/segmentation/factors-catalogue' as const,
  adminMarketClusters:                '/admin/market-clusters' as const,

  // Admin — Import pipeline
  adminImport:               '/admin/import' as const,
  adminImportHistory:        '/admin/import/history' as const,
  adminImportReportPattern:  '/admin/import/:batchId/report' as const,
  adminImportReport: (batchId: string) => `/admin/import/${batchId}/report` as const,

  // Admin — Validation référentiels (4.J.2)
  adminValidationSkus:   '/admin/validation/skus'   as const,
  adminValidationPdvs:   '/admin/validation/pdvs'   as const,
  adminValidationMerch:  '/admin/validation/merch'  as const,
  adminValidationBrands: '/admin/validation/brands' as const,
  adminValidationOrphans: '/admin/validation/orphans' as const,

  // Admin — Utilisateurs + Config (4.J.3)
  adminUsers:       '/admin/users'        as const,
  adminConfig:      '/admin/config'       as const,
  adminAlertConfig: '/admin/alert-config' as const, // V4.20.1 — calibrage seuils Ampleur P1
  adminPerfAlertConfig: '/admin/perf-alert-config' as const, // P9 Palier 4 — seuils alertes performance
  adminAuditLogs:   '/admin/audit-logs'   as const,
  adminMonitoring:  '/admin/monitoring'   as const, // Phase δ DECISIONS #163
  adminAlerting:    '/admin/alerting'    as const, // F1 UI Alerting DECISIONS #166 PHASE 2

  // Data Quality — post P3 (2026-07-25) : Pilotage seule page restante.
  // /data-quality sert Pilotage directement (plus de redirect intermédiaire).
  // dataQualityPilotage conservé comme alias pour compat liens externes/deep-links.
  dataQuality:              '/data-quality'              as const,
  dataQualityPilotage:      '/data-quality/pilotage'     as const,

  // Activités V2 (TRAIN-3-γ.1 DECISIONS #160)

  // Settings
  settings: '/settings' as const,
  // #14bis — page unique fusionnee (Parametres | Configuration | Utilisateurs)
  parametres: '/parametres' as const,

  // Welcome (ex-Alertes landing) — PHASE 3.0 UX Revolution DECISIONS #167
  welcome: '/welcome' as const,

  // Phénomènes P1 — V4.20 (module Marché). Surface OPS = module SFM Team Perf séparé.
  phenomenaMarche: '/phenomena/marche' as const,
  // V4.22 / B2 — Drill géré en POP-UP (PhenomenaDrillModal). Pas de route dédiée :
  // le pop-up s'ouvre depuis la même page Marché, qui reste montée derrière le backdrop
  // flouté. Deep-link ?drill=<id> = backlog optionnel.

  // Centre de notifications — #12+13 P1.1. ⚠ AUCUN LIEN N'Y MÈNE À CE PALIER :
  // la landing est intouchable et la nav ne bouge pas. La cloche viendra en P1.6.
  notifications: '/notifications' as const,
} as const;

/** Labels affichés dans l'UI par route racine */
export const ROUTE_LABELS: Record<string, string> = {
  '/marche': 'Marché Retail / Wholesale',
  '/marche/retail': 'Marché Retail / Wholesale',    // Legacy redirect compat
  '/performance': 'Performance',
  '/comparator': 'Comparateur SKU',
  '/official-launches': 'Lancements officiels',
  '/brand-vs-brand': 'Brand vs Brand',
  '/lab': 'Self Service Lab',
  '/lab/hub': 'Hub Requêtes',
  '/geo-data': 'Geo-data',
  '/flashinfo': 'Flash Info',
  '/flashinfo/feed': 'Feed',
  '/flashinfo/create': 'Nouvelle activité',
  '/flashinfo/edit': 'Édition',
  '/referentiels': 'Référentiels',
  '/referentiels/pdv':           'PDV',
  '/referentiels/sku':           'SKU',
  '/referentiels/brands':        'Marques',
  '/referentiels/merchandisers': 'Merchandisers',
  '/referentiels/routings':      'Routings',
  '/admin': 'Back-office',
  '/admin/segmentation':                   'Segmentation',
  '/admin/segmentation/factors-catalogue': 'Catalogue facteurs',
  '/admin/market-clusters':               'Market Clusters',
  '/admin/import':          'Assistant import',
  '/admin/import/history':  'Historique imports',
  '/admin/validation/skus':   'Validation SKUs',
  '/admin/validation/pdvs':   'Validation PDVs',
  '/admin/validation/merch':  'Validation Merchs',
  '/admin/validation/brands': 'Validation Brands',
  '/admin/validation/orphans': 'RFID / Rawdata Issues',
  '/admin/users':       'Utilisateurs',
  '/admin/config':      'Configuration',
  '/admin/audit-logs':  'Audit Logs',
  '/admin/monitoring':  'Monitoring système',
  '/admin/alerting':    'Alertes système',
  '/data-quality':              'Data Quality',
  '/data-quality/pilotage':     'Pilotage Qualité',
  '/settings': 'Paramètres',
  '/welcome': 'Welcome',
  '/phenomena/marche': 'Phénomènes Marché',
  '/notifications': 'Centre de notifications',
};

/**
 * `#796` · `#839` — clés catalogue pour les libellés de fil d'Ariane.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠⚠ `#839` — **LA RÈGLE DE `#796` A ÉTÉ TENUE, PUIS DÉPASSÉE.**
 * ══════════════════════════════════════════════════════════════════════════════
 *   *Le `#796` n'admettait ici que les routes dont la valeur catalogue FR était
 *   **identique** au littéral — prudence juste à l'époque, mais qui a laissé
 *   **le même nom de module écrit trois fois avec trois valeurs** (T0 `#838`
 *   §G) : `Welcome` / `Welcome` / `Accueil`.*
 *
 *   *Décision Hkm 07/09 : **le catalogue avait raison.** Les routes sans nom au
 *   catalogue en reçoivent un (**FR inchangé**, EN ajouté) ; les quatre qui
 *   divergeaient prennent le nom du glossaire. **`ROUTE_LABELS` reste** — il
 *   sert de repli et de source unique du FR pour les invariants `#800`.*
 *
 * ⚠ *Ce qui n'entre PAS : `Feed`, `SKU`, `Merchandisers`, `Routings`,
 *   `Back-office`, `Data Quality` — **FR == EN, donc littéraux** (`#800`).*
 */
export const ROUTE_LABEL_KEYS: Partial<Record<string, CleI18n>> = {
  // ── Modules ────────────────────────────────────────────────────────────────
  '/marche':                'nav.marcheRetailWholesale',
  '/marche/retail':         'nav.marcheRetailWholesale',
  '/performance':           'nav.performance',
  '/comparator':            'nav.comparateur',
  '/official-launches':     'nav.lancementsOfficiels',
  '/brand-vs-brand':        'nav.brandVsBrand',
  // ⚠ `#837` — le module s'appelle « Labo Libre service » depuis sa clôture ;
  //   le fil d'Ariane disait encore « Self Service Lab ». Il suit le module.
  '/lab':                   'nav.lab',
  '/lab/hub':               'lab.hubRequetes',
  // ⚠ `#818` — même motif : le module a été nommé « Geo-Données », le fil
  //   d'Ariane était resté sur « Geo-data ».
  '/geo-data':              'nav.geoData',
  '/flashinfo':             'nav.flashInfo',
  // ⚠ `#824` — `'/flashinfo/feed'` reste littéral : `Feed` a FR == EN (`#800`).
  '/flashinfo/create':      'flashinfo.filNouvelleActivite',
  '/flashinfo/edit':        'flashinfo.filEdition',
  '/phenomena/marche':      'nav.phenomenesMarche',
  '/data-quality/pilotage': 'nav.pilotageQualite',
  '/notifications':         'nav.centreNotifications',
  // ★1 — le catalogue avait raison, l'écran avait tort.
  '/welcome':               'nav.welcome',

  // ── Référentiels ───────────────────────────────────────────────────────────
  '/referentiels':          'nav.referentiels',
  '/referentiels/pdv':      'commun.pdv',
  '/referentiels/brands':   'commun.marques',
  // ⚠ `#800` — `SKU`, `Merchandisers`, `Routings` : FR == EN, ils restent
  //   littéraux et hors catalogue.

  // ── Administration ─────────────────────────────────────────────────────────
  '/admin/segmentation':                   'nav.segmentation',
  '/admin/segmentation/factors-catalogue': 'nav.catalogueFacteurs',
  '/admin/import':                         'nav.assistantImport',
  '/admin/import/history':                 'nav.historiqueImports',
  '/admin/users':                          'nav.utilisateurs',
  '/admin/config':                         'nav.configuration',
  '/admin/monitoring':                     'nav.monitoringSysteme',
  '/admin/alerting':                       'nav.alertesSysteme',
  '/admin/validation/skus':                'nav.validationSkus',
  '/admin/validation/pdvs':                'nav.validationPdvs',
  '/admin/validation/merch':               'nav.validationMerchs',
  '/admin/validation/brands':              'nav.validationBrands',
  '/settings':                             'nav.parametres',
  // ★1 — les trois derniers que l'écran contredisait.
  '/admin/audit-logs':         'nav.auditLogs',
  '/admin/market-clusters':    'nav.marketClusters',
  '/admin/validation/orphans': 'nav.rfidRawdata',
  // ⚠ `#800` — `Back-office`, `Data Quality` : FR == EN, littéraux.
};
